import React, { useState, useEffect } from 'react';
import { EnhancedArtifact } from '../../../types/artifacts';
import { ArtifactService } from '../../../services/api/artifactService';
import { ArtifactTypeService } from '../../../services/api/artifactTypeService';

export interface ArtifactDetailsViewProps {
  artifact: EnhancedArtifact;
  onEdit?: () => void;
  style?: React.CSSProperties;
}

export const ArtifactDetailsView: React.FC<ArtifactDetailsViewProps> = ({
  artifact,
  onEdit,
  style = {}
}) => {
  const [parentArtifact, setParentArtifact] = useState<EnhancedArtifact | null>(null);
  const [artifactTypeName, setArtifactTypeName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRelatedData();
  }, [artifact]);

  const loadRelatedData = async () => {
    setLoading(true);
    try {
      // Load parent artifact if parent_ref exists
      if (artifact.parent_ref) {
        const parent = await ArtifactService.resolveParentArtifact(artifact.parent_ref);
        setParentArtifact(parent);
      }
      
      // Load artifact type name
      if (artifact.artifact_type_id) {
        const typeName = await ArtifactTypeService.getArtifactTypeName(artifact.artifact_type_id);
        setArtifactTypeName(typeName);
      }
    } catch (error) {
      console.error('Failed to load related data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };
      case 'inactive':
        return { backgroundColor: '#f3f4f6', color: '#374151', borderColor: '#d1d5db' };
      case 'draft':
        return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
      case 'archived':
        return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
      default:
        return { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#bfdbfe' };
    }
  };

  return (
    <div style={style}>
      {/* Header with Edit Button */}
      {onEdit && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          paddingBottom: '8px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            Artifact Details
          </h3>
          <button
            onClick={onEdit}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
        </div>
      )}

      {/* Basic Information Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div>
          <h5 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '12px', 
            fontWeight: '500',
            color: '#6b7280'
          }}>
            Name
          </h5>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#1f2937',
            fontWeight: '500'
          }}>
            {artifact.name || 'Unnamed'}
          </p>
        </div>
        
        <div>
          <h5 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '12px', 
            fontWeight: '500',
            color: '#6b7280'
          }}>
            Artifact Type
          </h5>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#1f2937'
          }}>
            {loading ? 'Loading...' : (artifactTypeName || artifact.type || 'Unknown')}
          </p>
        </div>
      </div>

      {/* Description */}
      {artifact.description && (
        <div style={{ marginBottom: '16px' }}>
          <h5 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '12px', 
            fontWeight: '500',
            color: '#6b7280'
          }}>
            Description
          </h5>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: '#4b5563',
            lineHeight: '1.5'
          }}>
            {artifact.description}
          </p>
        </div>
      )}

      {/* Status */}
      {artifact.status && (
        <div style={{ marginBottom: '16px' }}>
          <h5 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '12px', 
            fontWeight: '500',
            color: '#6b7280'
          }}>
            Status
          </h5>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: '500',
            border: '1px solid',
            ...getStatusColor(artifact.status)
          }}>
            {artifact.status}
          </span>
        </div>
      )}

      {/* Parent Information */}
      {artifact.parent_ref && (
        <div style={{ marginBottom: '16px' }}>
          <h5 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '12px', 
            fontWeight: '500',
            color: '#6b7280'
          }}>
            Parent Artifact
          </h5>
          {loading ? (
            <p style={{ 
              margin: 0, 
              fontSize: '13px', 
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              Loading parent...
            </p>
          ) : parentArtifact ? (
            <div style={{
              padding: '8px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ 
                margin: '0 0 4px 0', 
                fontSize: '13px', 
                fontWeight: '500',
                color: '#1f2937'
              }}>
                {parentArtifact.name}
              </p>
              {parentArtifact.description && (
                <p style={{ 
                  margin: 0, 
                  fontSize: '12px', 
                  color: '#6b7280'
                }}>
                  {parentArtifact.description}
                </p>
              )}
            </div>
          ) : (
            <p style={{ 
              margin: 0, 
              fontSize: '13px', 
              color: '#dc2626',
              fontStyle: 'italic'
            }}>
              Parent artifact not found
            </p>
          )}
        </div>
      )}

      {/* Metadata */}
      {artifact.artifact_metadata && Object.keys(artifact.artifact_metadata).length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h5 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '12px', 
            fontWeight: '500',
            color: '#6b7280'
          }}>
            Metadata
          </h5>
          <div style={{
            padding: '8px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <pre style={{
              margin: 0,
              fontSize: '11px',
              color: '#6b7280',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace'
            }}>
              {JSON.stringify(artifact.artifact_metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        padding: '12px',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        fontSize: '11px',
        color: '#6b7280'
      }}>
        <div>
          <span style={{ fontWeight: '500' }}>Created:</span>{' '}
          {formatDate(artifact.created_at || '')}
        </div>
        <div>
          <span style={{ fontWeight: '500' }}>Updated:</span>{' '}
          {formatDate(artifact.updated_at || '')}
        </div>
      </div>
    </div>
  );
};
